import Location from "../../../hooks/Location";
import { useFirestoreGeneral, useFirestoreGeneralOrderBy } from "../../../firebase/useFirestore.js";
import PageHeader from "../../../components/layout/PageHeader.jsx";
import EventFieldMeta from "../../../components/events/EventFieldMeta.jsx";
import orderResultsByFieldOrder from "../../../helpers/orderResultsByFieldOrder";
import { useMemo } from "react";
import PersonaMeta from "../../../components/personas/PersonaMeta.jsx";
import PersonaNameList from "../../../components/personas/PersonaNameList.jsx";
import participantsFromResults from "../../../helpers/personas/participantsFromResults.js";
import OutputMeta from "../../../components/outputs/OutputMeta.jsx";
import TargetgroupMeta from "../../../components/stakeholders/TargetgroupMeta.jsx";
import enToNlDate from "../../../utils/enToNlDate";
import SubTargetgroupMeta from "../../../components/stakeholders/SubTargetgroupMeta.jsx";

const EventResultsDetail = () => {
  // Hooks
  const sessionID = Location()[3];
   const options = { year: 'numeric', month: 'numeric', day: 'numeric'};

  // Firestore
  const eventResults = useFirestoreGeneral("EventResults", "SessionID", sessionID || "");
  const subTargetgroups = useFirestoreGeneral("EventResults", "Type", "subtargetgroup");

  // The session's results carry the EventID — use it to fetch the form's field order.
  const eventId = useMemo(() => {
    if (!Array.isArray(eventResults)) return "";
    return eventResults.find((r) => r?.EventID)?.EventID || "";
  }, [eventResults]);

  // Same query EventOpen uses, so the order matches the form exactly.
  const eventFields = useFirestoreGeneralOrderBy("EventFields", "EventId", eventId || "", "Position", "asc");

  // Group results by FieldID (and sort each group by Timestamp desc)
  const groupedResults = useMemo(() => {
    if (!Array.isArray(eventResults)) return [];

    const byField = eventResults.reduce((acc, r) => {
      const key = r?.FieldID || "unknown";
      if (!acc[key]) acc[key] = [];
      acc[key].push(r);
      return acc;
    }, {});

    // sort each group's results by Timestamp if available
    Object.values(byField).forEach(list => {
      list.sort((a, b) => {
        const aTs = a?.Timestamp?.toMillis?.() ? a.Timestamp.toMillis() : a?.Timestamp || 0;
        const bTs = b?.Timestamp?.toMillis?.() ? b.Timestamp.toMillis() : b?.Timestamp || 0;
        return bTs - aTs;
      });
    });

    // convert to mappable array
    return Object.entries(byField).map(([fieldId, results]) => ({ fieldId, results }));
  }, [eventResults]);

  // Order the field groups to match the form (EventOpen) field order.
  const orderedGroups = useMemo(
    () => orderResultsByFieldOrder(groupedResults, eventFields),
    [groupedResults, eventFields]
  );

  // Render helpers
  const renderResultValue = (type, value, outputId, participants) => {

    if (type === "outputs"){
        return (
        <div className="event-result-outputs-container">
            <OutputMeta id={outputId} />
            <p className="event-result-outputs-value-p-tag">{value}</p>
            {/* Deelnemers horen bij de output zelf, niet bij de activiteit. */}
            <PersonaNameList personaIds={participants} showLabel />
        </div>
        )
    } 
    
    if (type === "participants") {
      return (
        <PersonaMeta personaId={value} />
      )
    }

    if (type === "date") {
        return typeof(value) === "object" && value !== null ? value.toDate().toLocaleDateString("nl-NL", options) : enToNlDate(value);
    }

    if (type === "targetgroup") {
        return <div>

                    <TargetgroupMeta targetgroupID={value} />
                    <div className="subtargetgroup-results-container">
                      {subTargetgroups && subTargetgroups.filter(group => group.ParentTargetgroupID === value).map(group => (
                        <SubTargetgroupMeta key={group.ID} targetgroupID={group.Value} />
                      ))}
                    </div>

              </div>;
    }

    if (type === "open") {
        return <p>{value}</p>;
    }

    if (type === "scale") {
        return <p>{value}</p>;
    }


    if (type === "number") {
        return <p>{value}</p>;
    }

    if (type === "multiple-one" || type === "multiple-multiple") {
        return <p>{value}</p>;
    }
  };

  return (
    <div className="main">
      <div className="main-container">
        <PageHeader
          heading={"Activiteit antwoorden"}
          description={`Op deze pagina kun je de antwoorden van deze activiteit bekijken.`}
          AI={false}
        />
        <div className="event-results-detail-container">
            {!eventResults && <p>Gegevens laden…</p>}
            {eventResults && orderedGroups.length === 0 && <p>Geen resultaten gevonden.</p>}

            {orderedGroups.map(group => (
            <div key={group.fieldId} className="dashboard-container">
                <h3>
                <EventFieldMeta fieldId={group.fieldId} />
                </h3>

                <div>
                {group.results.map((res) => (
                    <div
                    key={res?.ID}
                    >
                    <div>{renderResultValue(res?.Type, res?.Value, res?.OutputID, participantsFromResults([res]))}</div>
                    </div>
                ))}
                </div>
            </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default EventResultsDetail;

